/**
 * react-query setup + the query-key factory.
 *
 * Keys are namespaced by workspace so switching workspaces never shows another
 * tenant's cached data. Always build keys with `qk.*` — never inline arrays.
 */

import { QueryClient } from '@tanstack/react-query';
import type { PlatformId, PostStatus } from '../../shared/types';
import { ApiError } from './api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Never retry auth/validation/not-implemented errors.
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

type Ws = string | null | undefined;

export const qk = {
  me: () => ['me'] as const,
  setupState: () => ['setup-state'] as const,

  workspaces: () => ['workspaces'] as const,
  workspace: (id: Ws) => ['workspace', id] as const,
  members: (id: Ws) => ['workspace', id, 'members'] as const,
  invites: (id: Ws) => ['workspace', id, 'invites'] as const,
  invitePreview: (token: string) => ['invite-preview', token] as const,
  businessProfile: (id: Ws) => ['workspace', id, 'business-profile'] as const,

  styleSamples: (ws: Ws) => ['ws', ws, 'style', 'samples'] as const,
  styleProfile: (ws: Ws) => ['ws', ws, 'style', 'profile'] as const,

  accounts: (ws: Ws) => ['ws', ws, 'accounts'] as const,

  mediaList: (ws: Ws, filters?: { kind?: string; search?: string }) =>
    ['ws', ws, 'media', filters ?? {}] as const,
  media: (ws: Ws, id: string) => ['ws', ws, 'media', id] as const,

  posts: (ws: Ws, filters?: { status?: PostStatus; platform?: PlatformId; search?: string }) =>
    ['ws', ws, 'posts', filters ?? {}] as const,
  post: (ws: Ws, id: string) => ['ws', ws, 'post', id] as const,

  calendar: (ws: Ws, from: string, to: string) => ['ws', ws, 'calendar', from, to] as const,

  notifications: (ws: Ws, unreadOnly?: boolean) =>
    ['ws', ws, 'notifications', Boolean(unreadOnly)] as const,

  adminOverview: () => ['admin', 'overview'] as const,
  adminUsers: (search?: string) => ['admin', 'users', search ?? ''] as const,
  adminActivity: (workspaceId?: string) => ['admin', 'activity', workspaceId ?? ''] as const,
  adminAiUsage: (kind?: string) => ['admin', 'ai-usage', kind ?? ''] as const,
} as const;

/** Drop every cached query for a workspace (used on workspace switch / logout). */
export function clearWorkspaceQueries(workspaceId: string): void {
  queryClient.removeQueries({ queryKey: ['ws', workspaceId] });
}

export function clearAllQueries(): void {
  queryClient.clear();
}
